import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PrepaidPurchaseStatus } from '../../../../models/prepaid-wash-book.models';
import {
  emptyPrepaidRedemptionRecipient,
  PrepaidRedemptionRecipientForm,
} from '../../../../models/prepaid-redemption-recipient.util';
import {
  expandPrepaidPurchaseStatusToRedemptionSlots,
  formatPrepaidRedemptionSlotSubline,
  getPrepaidPurchaseStatusSummaryCards,
  isPrepaidPurchaseFullyRedeemed,
  PrepaidRedemptionSlot,
  PrepaidRedemptionSlotTab,
} from '../../../../models/prepaid-purchase-status.util';
import {
  CUSTOMER_EMAIL_MAX_LENGTH,
  CUSTOMER_NAME_MAX_LENGTH,
  CUSTOMER_PHONE_FORMATTED_MAX_LENGTH,
  sanitizeCustomerEmailInput,
  sanitizeCustomerNameInput,
  sanitizeCustomerPhoneInputAllowFormatting,
  validateCustomerEmail,
  validateCustomerName,
  validateCustomerPhone,
} from '../../../../shared/validators/customer-contact-field.rules';

type RecipientContactField = 'phone' | 'email' | 'firstName' | 'lastName';

@Component({
  selector: 'app-prepaid-redemption',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './prepaid-redemption.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./prepaid-redemption.component.scss'],
})
export class PrepaidRedemptionComponent {
  @Input() status: PrepaidPurchaseStatus | null = null;
  @Input() loading = false;
  @Input() error: string | null = null;
  @Input() selectedSlotKeys: string[] = [];
  @Input() redeemedSlots: PrepaidRedemptionSlot[] = [];
  @Input() loadingRedemptions = false;
  @Input() redemptionsError: string | null = null;
  @Input() recipient: PrepaidRedemptionRecipientForm = emptyPrepaidRedemptionRecipient();
  /**
   * CWS-1881: a plate-level refusal, chiefly "this plate belongs to another customer"
   * (VEHICLE_PLATE_TAKEN). Shown against the plate box so the attendant can see which field
   * to fix, instead of only a sentence in the footer.
   */
  @Input() plateError = '';
  @Input() phoneError = '';
  @Input() emailError = '';
  @Input() firstNameError = '';
  @Input() lastNameError = '';
  @Input() phoneSearchResults: Record<string, any>[] = [];
  @Input() showPhoneDropdown = false;
  @Input() isSearchingPhone = false;

  @Output() selectionChange = new EventEmitter<string[]>();
  @Output() recipientChange = new EventEmitter<PrepaidRedemptionRecipientForm>();
  @Output() recipientFieldTouched = new EventEmitter<void>();
  @Output() recipientPlateInput = new EventEmitter<string>();
  @Output() recipientPhoneInput = new EventEmitter<string>();
  @Output() recipientPhoneSearchSelect = new EventEmitter<Record<string, any>>();
  @Output() recipientPhoneBlur = new EventEmitter<void>();
  @Output() retryLoad = new EventEmitter<void>();
  @Output() redemptionTabOpened = new EventEmitter<void>();
  @Output() retryLoadRedemptions = new EventEmitter<void>();

  readonly nameMaxLength = CUSTOMER_NAME_MAX_LENGTH;
  readonly phoneMaxLength = CUSTOMER_PHONE_FORMATTED_MAX_LENGTH;
  readonly emailMaxLength = CUSTOMER_EMAIL_MAX_LENGTH;

  activeTab: PrepaidRedemptionSlotTab = 'active';

  private readonly localFieldErrors: Record<RecipientContactField, string> = {
    phone: '',
    email: '',
    firstName: '',
    lastName: '',
  };
  private readonly touchedFields = new Set<RecipientContactField>();

  get bookName(): string {
    return this.status?.book_name?.trim() || 'Prepaid Washbook';
  }

  get selectedCount(): number {
    return this.selectedSlotKeys.length;
  }

  get summaryCards() {
    return getPrepaidPurchaseStatusSummaryCards(this.status);
  }

  get activeSlots(): PrepaidRedemptionSlot[] {
    return expandPrepaidPurchaseStatusToRedemptionSlots(this.status).filter(
      s => s.tab === 'active'
    );
  }

  get visibleSlots(): PrepaidRedemptionSlot[] {
    return this.activeTab === 'active' ? this.activeSlots : this.redeemedSlots;
  }

  get showRedeemedListLoading(): boolean {
    return this.activeTab === 'redeemed' && this.loadingRedemptions;
  }

  get showRedeemedListError(): boolean {
    return this.activeTab === 'redeemed' && !!this.redemptionsError && !this.loadingRedemptions;
  }

  get showFullyRedeemedMessage(): boolean {
    return this.activeTab === 'active' && isPrepaidPurchaseFullyRedeemed(this.status);
  }

  setRedemptionTab(tab: PrepaidRedemptionSlotTab): void {
    this.activeTab = tab;
    if (tab === 'redeemed' && this.shouldRequestRedemptions()) {
      this.redemptionTabOpened.emit();
    }
  }

  private shouldRequestRedemptions(): boolean {
    return !this.loadingRedemptions && this.redeemedSlots.length === 0 && !this.redemptionsError;
  }

  isSlotSelected(slotKey: string): boolean {
    return this.selectedSlotKeys.includes(slotKey);
  }

  /**
   * CWS-1880: one wash per redemption.
   *
   * The slot list offered checkboxes while the purchase-card picker next to it used radios,
   * so the same attendant met two different controls for the same decision depending on how
   * they arrived. Selection is now single: picking a slot replaces whatever was selected.
   *
   * Kept as an idempotent set rather than a toggle, matching radio semantics — a browser
   * fires no change event when an already-checked radio is clicked, so re-selecting must not
   * clear the choice.
   */
  onSlotToggle(slot: PrepaidRedemptionSlot): void {
    if (slot.tab !== 'active' || this.isSlotSelected(slot.slotKey)) {
      return;
    }
    this.selectionChange.emit([slot.slotKey]);
  }

  /** Groups the slot radios so the browser enforces single select within this purchase. */
  get slotRadioGroupName(): string {
    return `prepaid-redemption-slot-${this.status?.prepaid_purchase_id ?? 'current'}`;
  }

  formatSlotSubline(slot: PrepaidRedemptionSlot): string {
    return formatPrepaidRedemptionSlotSubline(slot);
  }

  onRetry(): void {
    this.retryLoad.emit();
  }

  onRetryRedemptions(): void {
    this.retryLoadRedemptions.emit();
  }

  /** One shared recipient form under the first selected active slot. */
  showRecipientForSlot(slotKey: string): boolean {
    if (!this.isSlotSelected(slotKey) || this.selectedCount === 0) {
      return false;
    }
    const firstSelected = this.activeSlots.find(s => this.isSlotSelected(s.slotKey));
    return firstSelected?.slotKey === slotKey;
  }

  getFieldError(field: RecipientContactField): string {
    const parentError = this.getParentError(field);
    if (parentError) {
      return parentError;
    }
    if (this.touchedFields.has(field)) {
      return this.localFieldErrors[field];
    }
    return '';
  }

  hasFieldError(field: RecipientContactField): boolean {
    return !!this.getFieldError(field);
  }

  onRecipientPhoneInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const sanitized = sanitizeCustomerPhoneInputAllowFormatting(input.value);
    if (sanitized !== input.value) {
      input.value = sanitized;
    }
    this.onRecipientContactFieldInput('phone', sanitized);
    this.recipientPhoneInput.emit(sanitized);
  }

  onRecipientPhoneBlur(): void {
    this.onRecipientFieldBlur('phone');
    this.recipientPhoneBlur.emit();
  }

  onRecipientPhoneSearchSelect(customer: Record<string, any>): void {
    this.recipientPhoneSearchSelect.emit(customer);
  }

  recipientPhoneSearchLabel(customer: Record<string, any>): string {
    const first = String(customer['first_name'] ?? '').trim();
    const last = String(customer['last_name'] ?? '').trim();
    return [first, last].filter(Boolean).join(' ') || 'Customer';
  }

  onRecipientFirstNameInput(event: Event): void {
    this.onRecipientSanitizedInput(event, 'firstName', sanitizeCustomerNameInput);
  }

  onRecipientLastNameInput(event: Event): void {
    this.onRecipientSanitizedInput(event, 'lastName', sanitizeCustomerNameInput);
  }

  onRecipientEmailInput(event: Event): void {
    this.onRecipientSanitizedInput(event, 'email', sanitizeCustomerEmailInput);
  }

  private onRecipientSanitizedInput(
    event: Event,
    field: RecipientContactField,
    sanitize: (value: string) => string
  ): void {
    const input = event.target as HTMLInputElement;
    const sanitized = sanitize(input.value);
    if (sanitized !== input.value) {
      input.value = sanitized;
    }
    this.onRecipientContactFieldInput(field, sanitized);
  }

  onRecipientFieldBlur(field: RecipientContactField): void {
    this.touchedFields.add(field);
    const value = String(this.recipient[field] ?? '');
    this.localFieldErrors[field] = this.validateContactField(field, value);
  }

  onRecipientPlateInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    this.recipientFieldTouched.emit();
    this.recipientChange.emit({ ...this.recipient, licensePlate: value });
    this.recipientPlateInput.emit(value);
  }

  private onRecipientContactFieldInput(field: RecipientContactField, value: string): void {
    this.recipientFieldTouched.emit();
    this.touchedFields.add(field);
    this.localFieldErrors[field] = this.validateContactField(field, value);
    this.recipientChange.emit({ ...this.recipient, [field]: value });
  }

  private getParentError(field: RecipientContactField): string {
    switch (field) {
      case 'phone':
        return this.phoneError;
      case 'email':
        return this.emailError;
      case 'firstName':
        return this.firstNameError;
      case 'lastName':
        return this.lastNameError;
    }
  }

  private validateContactField(field: RecipientContactField, value: string): string {
    switch (field) {
      case 'phone':
        return validateCustomerPhone(value);
      case 'email':
        return validateCustomerEmail(value);
      case 'firstName':
      case 'lastName':
        return validateCustomerName(value);
    }
  }
}
